import type Company from '~/types/Company';
import type User from '~/types/User';

import { type ActionTypes } from '~/actions';
import { type openCreditCardModal } from '~/actions/billing.actions';
import { ActionKeys } from '~/actions/constants';
import { distanceUnitMap } from '~/constants/distance';
import { weightUnitMap } from '~/constants/weight';
import { AssetType, type Asset } from '~/types/Asset';
import { type Entitlements } from '~/types/Entitlements';
import {
  getFailed,
  getNotRequested,
  getRefreshFailed,
  getRefreshSuccess,
  getRefreshing,
  getRequesting,
  getSuccess,
} from '~/utils/fetchable';

export interface Activation {
  ordersCount?: null | number;
}

export type NextPaymentModalAction = ReturnType<typeof openCreditCardModal>;

export type UserDraftRequests = {
  [T in AssetType]: Map<number, Requestable<null, string>>;
};

export type UserState = {
  readonly activation: Activation;
  readonly assetDrafts: UserDraftRequests;
  readonly assets: Refreshable<Asset[], string>;
  readonly company: Readonly<Maybe<Company>>;
  readonly entitlements: Readonly<Maybe<Entitlements>>;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly isLoadingCompany: boolean;
  readonly isRefreshingUser: boolean;
  readonly nextModalAction: Readonly<Maybe<NextPaymentModalAction>>;
  readonly showEmailVerificationModal: boolean;
  readonly showPromoApplicationNotice: boolean;
  readonly user: Readonly<Maybe<User>>;
};

const initializeUserAssetDrafts = (numAssets = 1): Map<number, Requestable<null, string>> => {
  let assetMap = new Map<number, Requestable<null, string>>();
  for (let i = 0; i < numAssets; i++) assetMap.set(i + 1, getNotRequested());

  return assetMap;
};

export const initialState: UserState = {
  activation: {
    ordersCount: null,
  },
  assetDrafts: {
    [AssetType.LOGO]: initializeUserAssetDrafts(),
    [AssetType.LOGO_BW]: initializeUserAssetDrafts(),
    [AssetType.PROMO]: initializeUserAssetDrafts(2),
    [AssetType.PROMO_DELIVERED]: initializeUserAssetDrafts(),
    [AssetType.RECOMMENDATION]: initializeUserAssetDrafts(4),
    [AssetType.RECOMMENDATION_DELIVERED]: initializeUserAssetDrafts(4),
    [AssetType.SPOTLIGHT_DELIVERED]: initializeUserAssetDrafts(),
    [AssetType.SPOTLIGHT_VIDEO_DELIVERED]: initializeUserAssetDrafts(),
  },
  assets: getNotRequested(),
  company: null,
  entitlements: null,
  isAuthenticated: false,
  isLoading: true,
  isLoadingCompany: false,
  isRefreshingUser: false,
  nextModalAction: null,
  showEmailVerificationModal: false,
  showPromoApplicationNotice: false,
  user: null,
};

const userReducer = (state: UserState = initialState, action: ActionTypes): UserState => {
  let assetDraftsByType;
  switch (action.type) {
    case ActionKeys.UPDATE_PACKAGE_DEFAULT_UNITS_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          defaultDistanceUnit: distanceUnitMap[action.payload.defaultDistanceUnit],
          defaultMassUnit: weightUnitMap[action.payload.defaultWeightUnit],
        } as UserState['user'],
      };
    case ActionKeys.USER_SET_UNAUTHENTICATED:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    case ActionKeys.USER_FETCH:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.USER_FETCH_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        isRefreshingUser: false,
        user: action.payload.user,
      };
    case ActionKeys.USER_FETCH_ERROR:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        isRefreshingUser: false,
      };
    case ActionKeys.USER_ENTITLEMENTS_FETCH_SUCCESS:
      return {
        ...state,
        entitlements: action.payload,
      };
    case ActionKeys.USER_ENTITLEMENTS_FETCH_ERROR:
      return {
        ...state,
        entitlements: [],
      };
    case ActionKeys.COMPANY_FETCH:
      return {
        ...state,
        isLoadingCompany: true,
      };
    case ActionKeys.COMPANY_FETCH_SUCCESS:
    case ActionKeys.COMPANY_UPDATE_SUCCESS:
      return {
        ...state,
        company: action.payload,
        isLoadingCompany: false,
      };
    case ActionKeys.COMPANY_FETCH_ERROR:
      return {
        ...state,
        isLoadingCompany: false,
      };
    case ActionKeys.UPDATE_USER_DEFAULT_SERVICE_LEVEL:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              defaultProvider: action.defaultProvider,
              defaultServiceLevel: action.defaultServiceLevel,
            }
          : null,
      };
    case ActionKeys.UPDATE_USER_DEFAULT_RETURN_SERVICE_LEVEL:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              defaultReturnProvider: action.defaultReturnProvider,
              defaultReturnServiceLevel: action.defaultReturnServiceLevel,
            }
          : null,
      };
    case ActionKeys.UPDATE_DEFAULT_ADDRESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              address: action.payload.address,
            }
          : null,
      };
    case ActionKeys.UPDATE_DEFAULT_RETURN_ADDRESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              addressReturn: action.payload.address,
            }
          : null,
      };
    case ActionKeys.USER_REFRESH:
      return {
        ...state,
        isRefreshingUser: true,
      };
    case ActionKeys.USER_ASSETS_FETCH:
      return {
        ...state,
        assets: getRefreshing(state.assets),
      };
    case ActionKeys.USER_ASSETS_FETCH_SUCCESS:
      return {
        ...state,
        assets: getRefreshSuccess(state.assets, action.payload.assets),
      };
    case ActionKeys.USER_ASSETS_FETCH_ERROR:
      return {
        ...state,
        assets: getRefreshFailed(state.assets, action.payload.errorCode),
      };

    case ActionKeys.USER_ASSET_UPLOAD_DRAFT:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT:
    case ActionKeys.USER_ASSET_REVERT_DRAFT:
    case ActionKeys.USER_ASSET_PUBLISH:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getRequesting());
      return {
        ...state,
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.USER_FIRST_LABEL_PURCHASED:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              hasPurchasedLabel: true,
            }
          : null,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
            }
          : null,
      };
    case ActionKeys.USER_ASSET_UPLOAD_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_REVERT_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_PUBLISH_SUCCESS:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getSuccess(null));
      return {
        ...state,
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.USER_ASSET_UPLOAD_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_REVERT_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_PUBLISH_ERROR:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getFailed(action.payload.errorCode));
      return {
        ...state,
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.UPDATE_ACTIVATION_DATA:
      return {
        ...state,
        activation: {
          ...state.activation,
          ...action.payload,
        },
      };
    case ActionKeys.SHOW_PROMO_APPLICATION_NOTICE:
      return {
        ...state,
        showPromoApplicationNotice: true,
      };
    case ActionKeys.DISMISS_PROMO_APPLICATION_NOTICE:
      return {
        ...state,
        showPromoApplicationNotice: false,
      };
    case ActionKeys.SHOW_EMAIL_VERIFICATION_MODAL:
      return {
        ...state,
        nextModalAction: action.payload.nextModalAction,
        showEmailVerificationModal: true,
      };
    case ActionKeys.CLOSE_EMAIL_VERIFICATION_MODAL:
      return {
        ...state,
        nextModalAction: null,
        showEmailVerificationModal: false,
      };
    default:
      return state;
  }
};

export default userReducer;
